import { Box, CircularProgress } from '@mui/material';
import { memo, useEffect } from 'react';

/**
 * The page loader is displayed in several points in the UI in different parts of the
 * DOM tree: the inner `CircularProgress` would start from the beginning at every remount
 * if we used it directly, which results in a flickering display.
 *
 * Use a two-component approach to preserve the animation state: `PageLoaderPlaceholder`
 * is always on the page, hidden visually and for screen readers.
 * Controllers that need to display the loader must use `PageLoader`, which shows/hides
 * the placeholder on mount and unmount, without unmounting it.
 */
export const PageLoaderPlaceholder = memo(() => {
  return (
    <Box
      data-role="page-loader"
      aria-hidden="true"
      sx={{
        display: 'flex',
        position: 'fixed',
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        alignItems: 'center',
        justifyContent: 'center',
        color: 'primary-light',
        backgroundColor: 'backdrops.light',
        visibility: 'hidden',
        zIndex: -1,
        '&.visible': {
          visibility: 'visible',
          zIndex: 1000,
        },
      }}
    >
      <CircularProgress color="inherit" />
    </Box>
  );
});

export const PageLoader = () => {
  useEffect(() => {
    const loader = document.querySelector('[data-role="page-loader"]');
    if (loader) {
      loader.setAttribute('aria-hidden', 'false');
      loader.classList.add('visible');
    }
    return () => {
      if (loader) {
        loader.setAttribute('aria-hidden', 'true');
        loader.classList.remove('visible');
      }
    };
  }, []);

  return <></>;
};
